import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  TextField,
  MenuItem,
  Button,
  Grid,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import axiosInstant from '../../../api';
import {
  addBrandTeamMemberEndpoint,
  updateBrandTeamMemberEndpoint,
} from '../../../api/endpoints';
import { IconCloseEye, IconEye } from '../../../utils/icons';
import { BANNED_EMAIL_DOMAINS } from '../../authV2/components/constants';
import { useUser } from '../../../hooks/useUser';

// Roles assignable through the invite/edit flow (mirrors the API's
// INVITABLE_BRAND_USERTYPES allowlist in controllers/brands.js). Brand
// admin promotion of an existing member stays on the dedicated "make admin"
// action. A brand has one admin: internal staff may invite a new user as
// admin only while the brand has none.
const ASSIGNABLE_ROLES = [
  { value: 'brand team member', label: 'Team Member' },
  { value: 'brand sales', label: 'Sales (RMN tools only)' },
];
const INVITE_ONLY_ROLES = [{ value: 'brand admin', label: 'Brand Admin' }];

// API error codes for accounts a brand admin may not take over.
const TEAM_ERROR_COPY: Record<string, string> = {
  UserAlreadyTeamMember: 'This person is already on the team.',
  SalesRoleNetworkOnly: 'The Sales role is only available on Enterprise RMN and Agency brands.',
  ExistingAccountNotBrandUser: 'This email belongs to an account that can\'t be added to a brand team. Contact support.',
  ExistingAccountHasDifferentRole: 'This email already has an account with a different role. Contact support to add them.',
  MemberManagedElsewhere: 'This person\'s account is managed outside this brand, so only their access permissions can be changed here.',
};

// Internal roles that may set a role on brand users. Brand admins inviting
// through their own settings page always create plain team members.
const ROLE_SETTER_ROLES = [
  'ADMIN',
  'PRO_TEAM_ADMIN',
  'ACCOUNT_MANAGER_ADMIN',
  'POST_PRODUCTION_ADMIN',
  'SALES_TEAM',
];

interface TeamMemberModalProps {
  open: boolean;
  onClose: () => void;
  user?: any;
  brandId: number;
  brandHasAdmin?: boolean;
  // A network brand (enterprise RMN / agency plan): only those get the Sales
  // role — the RMN tools it opens exist only there (the API refuses it
  // otherwise, SalesRoleNetworkOnly).
  networkBrand?: boolean;
  onSuccess: () => void;
}

const validationSchema = yup.object().shape({
  emailAddress: yup
    .string()
    .email('Invalid email')
    .required('Email is required')
    .test(
      'banned-domain',
      'Please use a business email, not a public domain like @gmail.com.',
      function (value) {
        if (!value) return true;
        const emailDomain = value.split('@')[1]?.toLowerCase();
        return !BANNED_EMAIL_DOMAINS.includes(emailDomain);
      }
    ),
  password: yup.string().when('isNew', {
    is: true,
    then: yup.string().required('Password is required'),
    otherwise: yup.string(),
  }),
  firstName: yup.string(),
  lastName: yup.string(),
});

export const TeamMemberModal: React.FC<TeamMemberModalProps> = ({
  open,
  onClose,
  user,
  brandId,
  brandHasAdmin = true,
  networkBrand = false,
  onSuccess,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const { role: requesterRole } = useUser();

  // Role select: internal admins only. Shown disabled on a brand admin row
  // (the API refuses to convert admins through this path — promotion/demotion
  // goes through the dedicated "make admin" action).
  const isRoleSetter = ROLE_SETTER_ROLES.includes(String(requesterRole));
  const targetIsBrandAdmin = !!user && user.usertype === 'brand admin';
  const canSetRole = isRoleSetter && !targetIsBrandAdmin;
  // Sales only on a network brand — or kept for an existing sales member so
  // their current role still shows.
  const salesAllowed = networkBrand || user?.usertype === 'brand sales';
  const assignable = ASSIGNABLE_ROLES.filter((r) => r.value !== 'brand sales' || salesAllowed);
  const roleHelp = (usertype: string): string | undefined => {
    if (usertype === 'brand admin') return 'The brand admin manages the team. A brand has one admin.';
    if (salesAllowed) return 'Sales users only see the RMN tools (PDP Audit, Sales Intel Prep Pack, Strategy).';
    return undefined;
  };
  const roleOptions =
    user || brandHasAdmin
      ? assignable
      : [...assignable, ...INVITE_ONLY_ROLES];

  const initialValues = {
    emailAddress: user?.emailAddress || '',
    password: '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    isNew: !user,
    sendEmailInvite: true,
    usertype:
      user?.usertype === 'brand sales' ? 'brand sales' : 'brand team member',
  };

  const handleSubmit = async (values: any, actions: any) => {
    try {
      const submitData = {
        emailAddress: values.emailAddress,
        firstName: values.firstName,
        lastName: values.lastName,
        sendEmailInvite: values.sendEmailInvite,
        ...(values.password && { password: values.password }),
        ...(canSetRole && { usertype: values.usertype }),
      };

      if (user) {
        // Edit existing user
        await axiosInstant.patch(
          updateBrandTeamMemberEndpoint(brandId, user.userid),
          submitData
        );
        toast.success('Team member updated successfully');
      } else {
        // Add new user
        await axiosInstant.post(
          addBrandTeamMemberEndpoint(brandId),
          submitData
        );
        toast.success('Team member added successfully');
      }

      actions.setSubmitting(false);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error(error);
      const data = error?.response?.data;
      const code = typeof data === 'string' ? data : data?.error;
      toast.error(TEAM_ERROR_COPY[code] || code || 'Failed to save team member');
      actions.setSubmitting(false);
    }
  };

  const getPasswordVisibilityIcon = (
    isVisible: boolean,
    onToggle: (e: boolean) => void
  ) => (
    <Box
      position="absolute"
      sx={{ right: 10, top: 16, cursor: 'pointer' }}
      onClick={() => onToggle(!isVisible)}
    >
      {isVisible ? <IconCloseEye /> : <IconEye stroke="#868686" />}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, pb: 1 }}>
        <Typography
          variant="h4"
          className="font-[CircularStd]"
          sx={{ fontSize: '2rem', fontWeight: 'bold' }}
        >
          {user ? 'Edit Team Member' : 'Add Team Member'}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            isSubmitting,
            isValid,
          }) => {
            // Disable button if form is invalid and user has interacted with required fields
            const hasTouchedRequiredFields = user 
              ? Boolean(touched.emailAddress) 
              : Boolean(touched.emailAddress && touched.password);
            const shouldDisable = isSubmitting || (!isValid && hasTouchedRequiredFields);
            
            return (
            <Form>
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="emailAddress"
                      label="Email*"
                      value={values.emailAddress}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.emailAddress && Boolean(errors.emailAddress)
                      }
                      helperText={touched.emailAddress && errors.emailAddress}
                      placeholder="name@example.com"
                    />
                  </Grid>

                  {!user && (
                    <Grid item xs={12}>
                      <div className="relative">
                        <TextField
                          fullWidth
                          name="password"
                          label="Password*"
                          type={showPassword ? 'text' : 'password'}
                          value={values.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.password && Boolean(errors.password)}
                          helperText={touched.password && errors.password}
                          placeholder="password123"
                        />
                        {getPasswordVisibilityIcon(
                          showPassword,
                          setShowPassword
                        )}
                      </div>
                    </Grid>
                  )}

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      name="firstName"
                      label="First Name"
                      value={values.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="First Name"
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      name="lastName"
                      label="Last Name"
                      value={values.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Last Name"
                    />
                  </Grid>

                  {isRoleSetter && (
                    <Grid item xs={12}>
                      {targetIsBrandAdmin ? (
                        <TextField
                          fullWidth
                          label="Role"
                          value="Brand Admin"
                          disabled
                          helperText="Admins can't be converted here — use the admin action on the team list."
                        />
                      ) : (
                        <TextField
                          select
                          fullWidth
                          name="usertype"
                          label="Role"
                          value={values.usertype}
                          onChange={handleChange}
                          helperText={roleHelp(values.usertype)}
                        >
                          {roleOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    </Grid>
                  )}

                  {!user && (
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="sendEmailInvite"
                            checked={values.sendEmailInvite}
                            onChange={handleChange}
                            color="primary"
                          />
                        }
                        label="Send email invite to new user"
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Box
                      sx={{
                        mt: 2,
                        display: 'flex',
                        justifyContent: 'flex-start',
                      }}
                    >
                      <Button
                        variant="contained"
                        type="submit"
                        disabled={shouldDisable}
                        sx={{
                          bgcolor: 'var(--accent)',
                          '&:hover': {
                            bgcolor: '#8D703F',
                          },
                          '&:disabled': {
                            bgcolor: 'var(--accent)',
                            color: '#FFF',
                            opacity: 0.5,
                          },
                        }}
                      >
                        {user ? 'Save' : 'Invite'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
